import React from "react";
import { useTranslation } from "react-i18next";

export default function MessageModal({ open, message, onClose, onMarkReadToggle }) {
  const { t } = useTranslation();
  if (!open || !message) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[40px] max-w-3xl w-full shadow-2xl transform transition-all animate-scale-in overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-10 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative z-10">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center">
                    <i className="fas fa-envelope-open-text text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-3xl font-black tracking-tight">{message.name}</h3>
                    <div className="flex items-center gap-3 mt-2 text-white/80 text-sm font-medium">
                      <span className="flex items-center gap-2">
                        <i className="fas fa-envelope text-xs"></i>
                        {message.email}
                      </span>
                      {message.phone && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-2">
                            <i className="fas fa-phone text-xs"></i>
                            {message.phone}
                          </span>
                        </>
                      )}
                    </div>
                    {message.country && (
                      <div className="mt-2 flex items-center gap-2 text-white/70 text-xs">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{message.country}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <div className="flex items-center gap-3">
                  <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest backdrop-blur-md ${message.is_read
                    ? 'bg-green-500/80 text-white'
                    : 'bg-yellow-500/80 text-white'
                    }`}>
                    <i className={`fas ${message.is_read ? 'fa-check-double' : 'fa-circle'} mr-2`}></i>
                    {message.is_read ? t('modals.message.read', 'Read') : t('modals.message.unread', 'Unread')}
                  </div>
                  <div className="px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-widest">
                    <i className="fas fa-clock mr-2"></i>
                    {new Date(message.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-10">
          <div className="mb-8">
            <label className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 ml-2 block">
              <i className="fas fa-message mr-2"></i>
              {t('modals.message.message_content', 'Message Content')}
            </label>
            <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap font-medium">
                {message.message}
              </p>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex items-center justify-between p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl border border-gray-100">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <i className="fas fa-calendar-alt"></i>
              <span className="font-bold">
                {t('modals.message.sent', {
                  date: new Date(message.created_at).toLocaleString()
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <i className="fas fa-hashtag"></i>
              <div className="font-mono font-bold flex items-center gap-1">
                <span>{t('modals.message.id_label', "ID:")}</span>
                <span>{message.id}</span>
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className="flex gap-4 mt-8">
            <button
              onClick={() => onMarkReadToggle(message)}
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-600/20 hover:scale-[1.02] active:scale-95 transition-all"
            >
              <i className={`fas ${message.is_read ? 'fa-envelope' : 'fa-check-double'} mr-2`}></i>
              {message.is_read ? t('modals.message.mark_unread', 'Mark as Unread') : t('modals.message.mark_read', 'Mark as Read')}
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] active:scale-95"
            >
              <i className="fas fa-times mr-2"></i>
              {t('modals.message.close', 'Close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
